import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { Link, useParams } from 'react-router-dom'
import Sprite from '~components/Sprite'
import moment from 'moment'
import OrderAccordionItem from '~components/account/OrderAccordionItem'
import { displayPrice } from '~helpers/price'
import { groupBy, sortBy } from 'lodash'
import { pageView } from '~helpers/ga'

const Order = ({ account }) => {
  const params = useParams()
  const orderId = params.order_id
  const [order, setOrder] = useState([])
  const [products, setProducts] = useState([])
  const [accordions, setAccordions] = useState([])

  useEffect(() => {
    pageView()

    axios.get('/api/account/order', {
      params: { order_id: orderId } // Use the orderId from useParams
    }).then(response => {
      setOrder(response.data.order)
      setProducts(response.data.products)
    })
  }, [orderId])

  useEffect(() => setAccordionList(), [products])

  const setAccordionList = () => {
    const items = groupBy(products, 'name')
    const newAccordions = Object.keys(items).map(key => ({
      id: key,
      expand: !!account.stockist.expanded_cart
    }))

    setAccordions(newAccordions)
  }

  const toggle = (id) => {
    setAccordions(prevAccordions =>
      prevAccordions.map(accordion =>
        accordion.id === id ? { ...accordion, expand: !accordion.expand } : accordion
      )
    )
  }

  const toggleExpandAll = (expand) => {
    setAccordions(prevAccordions =>
      prevAccordions.map(accordion => ({
        ...accordion,
        expand
      })))
  }

  const anyAccordionsOpen = () => accordions.some(accordion => accordion.expand)
  const productItems = groupBy(products, 'name')

  return (
    <>
      <div className="mt-10">
        <div className="flex flex-wrap items-center -mx-1 -my-4 md:-mx-4 md:-my-1 md:flex-no-wrap xxl:-mx-10">
          <div className="flex-shrink-0 px-1 my-4 md:px-4 xxl:px-10">
            <Link
              to="/account/orders"
              className="group hover:bg-black focus:bg-black w-12 h-12 flex flex-col items-center border border-charcoal"
            >
              <span
                className="stroke-charcoal w-4 h-4 block transform rotate-90 m-auto icon icon-stroke group-hover:stroke-white group-focus:stroke-white"
                aria-hidden
              >
                <Sprite name="chevronAlt" />
              </span>
            </Link>
          </div>

          {order && (
            <div className="w-full px-1 my-4 md:px-4 md:my-1 md:flex md:flex-wrap md:justify-center xxl:px-10 xxl:w-10/12">
              <div className="-m-1 md:-mx-3 flex flex-wrap w-full md:items-center lg:justify-between">
                <span className="block font-bold w-full px-1 my-1 md:w-auto md:px-3">
                  ID&nbsp;
                  {order.id}
                </span>

                <span className="block font-bold w-full px-1 my-1 md:w-auto md:px-3">
                  Date&nbsp;
                  {moment(order.date_created).format('DD-MM-YYYY')}
                </span>

                <span className="block font-bold w-full px-1 my-1 md:w-auto md:px-3">
                  Amount&nbsp;
                  {displayPrice(order.total_inc_tax, order.currency_code)}
                </span>

                <span className="block font-bold w-full px-1 my-1 md:w-auto md:px-3">
                  Status&nbsp;
                  {order.status}
                </span>

                <span className="block font-bold w-full px-1 my-1 md:w-auto md:px-3">
                  Customer Ref&nbsp;
                  {order.customer_message}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {order && (
        <div className="w-full overflow-x-auto mt-10">
          <div className="grid grid-cols-7 w-full mb-12 min-w-250 text-xs xl:text-base">
            <div className="grid grid-cols-7 col-span-7">
              <span className="p-3 md:p-4 font-bold">
                Brand
              </span>

              <span className="p-3 md:p-4 font-bold">
                Product name
              </span>

              <span className="p-3 md:p-4 font-bold">
                Style number
              </span>

              <span className="p-3 md:p-4 font-bold">
                Colour
              </span>

              <span className="p-3 md:p-4 font-bold">
                Total unit qty
              </span>

              <span className="p-3 md:p-4 font-bold">
                Total price
              </span>

              <button
                type="button"
                className="p-3 md:p-4 font-bold bg-transparent hover:underline focus:underline text-left"
                onClick={() => {
                  const expand = !anyAccordionsOpen()
                  toggleExpandAll(expand)
                }}
              >
                {anyAccordionsOpen() ? 'Collapse all' : 'Expand all'}
              </button>
            </div>

            {accordions.map((accordion, index) => {
              const group = sortBy(productItems[accordion.id], item => item.product_options.find(o => o.display_name === 'Size').value)

              return (
                <OrderAccordionItem
                  qty={productItems[accordion.id].quantity}
                  productName={accordion.id}
                  items={group}
                  currency_code={order.currency_code}
                  key={accordion.id}
                  expandPreference={accordion.expand}
                  toggle={() => toggle(accordion.id)}
                />
              )
            })}
          </div>
        </div>
      )}
    </>
  )
}

Order.propTypes = {
  account: PropTypes.object
}

const mapStateToProps = (state) => ({
  account: state.auth.account
})

export default connect(mapStateToProps)(Order)
